import React, { useState } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import {
  LayoutDashboard,
  BarChart3,
  CreditCard,
  Key,
  ChevronLeft,
  ChevronRight,
  LogOut,
  type LucideIcon,
} from 'lucide-react';
import { useAuthContext } from '../context/AuthContext';

interface NavItem {
  path: string;
  name: string;
  icon: LucideIcon;
}

const navItems: NavItem[] = [
  { path: '/customer/dashboard', name: 'Dashboard', icon: LayoutDashboard },
  { path: '/customer/usage', name: 'Usage Reports', icon: BarChart3 },
  { path: '/customer/billing', name: 'Billing', icon: CreditCard },
  { path: '/customer/api-keys', name: 'API Keys', icon: Key },
];

const CustomerLayout: React.FC = () => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const { logout, user } = useAuthContext();

  return (
    <div className="flex h-screen bg-figma-bg dark:bg-[#1a0f2e]">
      {/* Sidebar */}
      <div
        className={`h-screen flex flex-col bg-figma-bg dark:bg-[#1a0f2e] border-r border-figma-border transition-all duration-300 ${
          sidebarOpen ? 'w-sidebar' : 'w-20'
        }`}
      >
        <div className="flex items-center justify-between pt-10 pb-4 px-4">
          <span className="text-xl font-semibold text-primary-500 tracking-wide">
            {sidebarOpen ? 'NU DATA' : 'NU'}
          </span>
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-1.5 rounded-md text-figma-text-light hover:bg-figma-button-hover transition-colors"
          >
            {sidebarOpen ? <ChevronLeft size={18} /> : <ChevronRight size={18} />}
          </button>
        </div>

        {sidebarOpen && (
          <div className="px-4 pb-2 text-sm text-figma-text-light">
            {user?.email}
          </div>
        )}

        <div className="mx-4 border-t border-figma-border" />

        <nav className="flex-1 flex flex-col px-2 pt-2">
          <ul className="space-y-1">
            {navItems.map((item) => {
              const Icon = item.icon;
              return (
                <li key={item.path}>
                  <NavLink
                    to={item.path}
                    className={({ isActive }) =>
                      `flex items-center gap-4 h-14 rounded-lg text-body transition-all duration-200 ${
                        sidebarOpen ? 'px-8' : 'justify-center px-2'
                      } ${
                        isActive
                          ? 'bg-primary-500 text-white font-semibold'
                          : 'text-figma-text-light hover:bg-figma-button-hover'
                      }`
                    }
                  >
                    <Icon size={24} />
                    {sidebarOpen && <span>{item.name}</span>}
                  </NavLink>
                </li>
              );
            })}
          </ul>

          <div className="flex-1" />

          <button
            onClick={logout}
            className={`flex items-center gap-4 h-14 rounded-lg text-body text-figma-text-light hover:bg-figma-button-hover transition-all duration-200 mb-4 ${
              sidebarOpen ? 'px-8' : 'justify-center px-2'
            }`}
          >
            <LogOut size={24} />
            {sidebarOpen && <span>Logout</span>}
          </button>
        </nav>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto">
        <Outlet />
      </div>
    </div>
  );
};

export default CustomerLayout;
